// Shared password-strength rules. Used client-side for the live checklist
// and server-side for boundary validation so the API can't be bypassed by
// callers that skip the form.
//
// These are MUCKA's rules, and they are deliberately stricter than Supabase's.
// Checked against the prod dashboard on 2026-08-06 (Authentication → Sign In /
// Providers → Email):
//
//   Minimum password length ......... 6      (we require 8)
//   Password requirements ........... unset  (GoTrue enforces NO character
//                                             classes — the mixed-case/number/
//                                             symbol rules below are ours alone)
//   Prevent use of leaked passwords . ON     (prod only; OFF on dev)
//
// Two things follow. First, do NOT narrow these rules to "what GoTrue accepts"
// — it accepts anything 6+ characters, so narrowing only locks users out of
// passwords the server would take. A `£` is a perfectly good symbol here.
// Second, the ONLY way a password that passes this checklist can still be
// rejected by the server is the leaked-password check, and that is enabled in
// production but not in dev — so this class of bug cannot be reproduced
// locally. See `usePwnedPassword`, which mirrors that check client-side.
//
// If the dashboard minimum ever rises above 8, change PASSWORD_MIN_LENGTH here
// and nowhere else — and note that `weakPasswordCopy`
// (src/lib/auth/weak-password.ts) reads the real number back out of Supabase's
// own rejection, so the message stays truthful even while this constant is stale.

/** Minimum length. Must match Supabase Auth → Policies → Minimum password length. */
export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordRuleStatus {
  length: boolean;
  mixedCase: boolean;
  number: boolean;
  symbol: boolean;
}

export interface PasswordEvaluation extends PasswordRuleStatus {
  allMet: boolean;
}

export const PASSWORD_RULES: Array<{ key: keyof PasswordRuleStatus; label: string }> = [
  { key: "length", label: `Use ${PASSWORD_MIN_LENGTH} or more characters` },
  { key: "mixedCase", label: "Use upper and lower case letters" },
  { key: "number", label: "Use a number" },
  { key: "symbol", label: "Use a symbol" },
];

export function evaluatePassword(pw: string): PasswordEvaluation {
  const length = pw.length >= PASSWORD_MIN_LENGTH;
  const mixedCase = /[a-z]/.test(pw) && /[A-Z]/.test(pw);
  const number = /\d/.test(pw);
  const symbol = /[^A-Za-z0-9]/.test(pw);
  return {
    length,
    mixedCase,
    number,
    symbol,
    allMet: length && mixedCase && number && symbol,
  };
}
